
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import EmbedScript from '@/components/EmbedScript';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface InstallationKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  installationKey?: string;
  domain: string;
}

const InstallationKeyModal = ({ isOpen, onClose, installationKey, domain }: InstallationKeyModalProps) => {
  const [activeTab, setActiveTab] = useState<string>("standard");
  
  console.log("InstallationKeyModal rendering with key:", installationKey, "domain:", domain);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 gap-0 overflow-hidden border-0 flex flex-col">
        <DialogHeader className="p-6 pb-2 border-b flex-shrink-0">
          <div className="flex justify-between items-center w-full">
            <DialogTitle className="text-xl">Installation Instructions for {domain}</DialogTitle>
            <button 
              className="h-8 w-8 rounded-full inline-flex items-center justify-center text-muted-foreground hover:bg-muted"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <DialogDescription>
            Copy the appropriate installation code for your website.
          </DialogDescription>
          
          <Alert variant="destructive" className="mt-4 border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900/50">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <AlertDescription className="text-red-600 dark:text-red-400">
              <strong>IMPORTANT:</strong> Only include ONE configuration block in your entire website. 
              Including multiple instances of the widget will cause conflicts.
            </AlertDescription>
          </Alert>
        </DialogHeader>
        
        <div className="flex-grow flex flex-col overflow-hidden">
          <Tabs defaultValue="standard" value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
            <TabsList className="h-12 rounded-none border-b px-6 w-full gap-6 flex-shrink-0">
              <TabsTrigger 
                value="standard" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none"
              >
                Standard
              </TabsTrigger>
              <TabsTrigger 
                value="webflow" 
                className="data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none"
              >
                Webflow
              </TabsTrigger>
            </TabsList>
            
            <div className="flex-grow overflow-hidden">
              <TabsContent value="standard" className="m-0 h-full overflow-hidden">
                <div className="h-full p-6 pt-4 overflow-hidden">
                  <ScrollArea className="h-full pr-4">
                    <div className="pb-6">
                      {installationKey ? (
                        <EmbedScript installationKey={installationKey} installationType="standard" />
                      ) : (
                        <div className="p-4 border border-amber-200 rounded-md bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50">
                          <p className="text-amber-800 dark:text-amber-200 font-medium">No installation key available</p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">Please ensure your domain is registered and has an active installation key.</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </TabsContent>
              
              <TabsContent value="webflow" className="m-0 h-full overflow-hidden">
                <div className="h-full p-6 pt-4 overflow-hidden">
                  <ScrollArea className="h-full pr-4">
                    <div className="pb-6">
                      {installationKey ? (
                        <EmbedScript installationKey={installationKey} installationType="webflow" />
                      ) : (
                        <div className="p-4 border border-amber-200 rounded-md bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/50">
                          <p className="text-amber-800 dark:text-amber-200 font-medium">No installation key available</p>
                          <p className="text-sm text-amber-700 dark:text-amber-300">Please ensure your domain is registered and has an active installation key.</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InstallationKeyModal;
